import { Button, CloseButton, Group, Loader, Popover, Text, UnstyledButton } from "@mantine/core";
import { Sparkle, Upload } from "lucide-react";
import { useId, useState, type ReactElement, type ReactNode } from "react";

import type { Asset } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { useLibraryPages } from "../../../platform/media/library-pages";
import { MasonryGrid } from "../../../ui/MasonryGrid";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import classes from "./ad-copies.module.css";

export interface InspirationPopoverProps {
  teamId: string;
  brandId: string;
  chosenId: string | null;
  opened: boolean;
  onClose: () => void;
  onPick: (image: PickedImage) => void;
  anchor: (wrap: (element: ReactElement) => ReactElement) => ReactNode;
}

/**
 * Live's "Add inspiration" popover (`reference-popover`, capture advanced-234/28): the title and
 * an x, live's search, and the brand's library images in four columns with their kind's badge,
 * "No references found" when none; a pick closes it.
 */
export function InspirationPopover(props: InspirationPopoverProps) {
  const { teamId, brandId, chosenId, opened, onClose, onPick, anchor } = props;
  const titleId = useId();
  return (
    <Popover
      opened={opened}
      onChange={(next) => (next ? undefined : onClose())}
      position="left-start"
      offset={8}
      trapFocus
      returnFocus
    >
      {anchor((element) => (
        <Popover.Target>{element}</Popover.Target>
      ))}
      <Popover.Dropdown className={classes.inspirationDropdown} aria-labelledby={titleId}>
        {/* Mounted while open: each opening starts with no search. */}
        <Images
          titleId={titleId}
          teamId={teamId}
          brandId={brandId}
          chosenId={chosenId}
          onClose={onClose}
          onPick={(image) => {
            onPick(image);
            onClose();
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

interface ImagesProps {
  titleId: string;
  teamId: string;
  brandId: string;
  chosenId: string | null;
  onClose: () => void;
  onPick: (image: PickedImage) => void;
}

function Images({ titleId, teamId, brandId, chosenId, onClose, onPick }: ImagesProps) {
  const [search, setSearch] = useState("");
  const images = useLibraryPages(teamId, brandId, { kind: "image", trash: false, search });
  const items = images.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <div className={classes.inspiration}>
      <div className={classes.groupHeader}>
        <Text id={titleId} className={classes.listTitle}>
          Add inspiration
        </Text>
        <CloseButton aria-label="Close" onClick={onClose} />
      </div>
      <SubmitSearch label="Search" value={search} onSearch={setSearch} />
      <div className={classes.inspirationScroll}>
        {images.isPending ? <Loader size="sm" aria-label="Loading images" /> : null}
        {images.isError ? <ErrorAlert error={images.error} /> : null}
        {images.isSuccess && items.length === 0 ? (
          <Text className={classes.empty}>No references found</Text>
        ) : null}
        <MasonryGrid columns={4} label="Inspiration images">
          {items.map((asset) => (
            <ImageTile
              key={asset.id}
              asset={asset}
              chosen={asset.id === chosenId}
              onPick={() => onPick({ assetId: asset.id, url: pictureOf(asset), name: asset.name })}
            />
          ))}
        </MasonryGrid>
        {images.hasNextPage ? (
          <Group justify="center" mt="md">
            <Button
              variant="outline"
              loading={images.isFetchingNextPage}
              onClick={() => void images.fetchNextPage()}
            >
              Load more
            </Button>
          </Group>
        ) : null}
      </div>
    </div>
  );
}

/** A library image to pick, with live's badge: generated (a spark) or uploaded (an arrow). */
function ImageTile(props: { asset: Asset; chosen: boolean; onPick: () => void }) {
  const { asset, chosen, onPick } = props;
  const url = pictureOf(asset);
  return (
    <UnstyledButton
      className={classes.inspirationTile}
      aria-label={asset.name}
      aria-pressed={chosen}
      onClick={onPick}
    >
      {url === null ? (
        <span className={classes.tilePicture} aria-hidden />
      ) : (
        <img src={url} alt="" className={classes.inspirationPicture} />
      )}
      <span className={classes.inspirationBadge} aria-hidden>
        {asset.source === "generation" ? <Sparkle size={12} /> : <Upload size={12} />}
      </span>
    </UnstyledButton>
  );
}
